import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons/index.tsx';
import { iconClock } from '@sorare/core/src/atoms/icons/system/iconClock.ts';
import { HalfCircularProgressBar } from '@sorare/core/src/atoms/loader/CircularProgressIndicator/HalfCircularProgressBar/index.tsx';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import { Chip } from '@sorare/core/src/atoms/ui/Chip/index.tsx';
import { ANY_SPORT_SERIES_MISSIONS_PLAY } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useConfigContext } from '@sorare/core/src/contexts/config/index.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { TimeLeft } from '@sorare/core/src/contexts/ticker/TimeLeft/index.tsx';

import { IconWrapper, MissionButton } from '../ui.tsx';
import type { DailyActionTaskWidget_dailyActionTask } from './__generated__/index.graphql.ts';
import playIcon from './assets/playIcon.webp';

const StyledChip = styled(Chip)`
  position: absolute;
  top: 84%;
  left: 50%;
  transform: translate(-50%, 0px);
`;

type Props = { dailyActionTask: DailyActionTaskWidget_dailyActionTask };
export const DailyActionTaskWidget = ({ dailyActionTask }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const {
    so5: { dailyTasksExpiresAt },
  } = useConfigContext();

  return (
    <Badge
      badgeContent={
        (dailyActionTask.subtasks || []).filter(
          subtask => subtask.aasmState === ManagerProgressionTaskState.COMPLETED
        ).length
      }
      overlap="circular"
    >
      <MissionButton
        to={generateSportContextPath(ANY_SPORT_SERIES_MISSIONS_PLAY)}
      >
        <HalfCircularProgressBar
          size={6}
          progresses={(dailyActionTask.subtasks || [])
            .map(subtask =>
              subtask.aasmState === ManagerProgressionTaskState.CLAIMED ||
              subtask.aasmState === ManagerProgressionTaskState.COMPLETED
                ? 1
                : 0
            )
            .sort((a, b) => b - a)}
          colors={[
            { from: '#7029FF', to: '#7029FF' },
            { from: '#7029FF', to: '#7029FF' },
            { from: '#7029FF', to: '#7029FF' },
          ]}
        />
        <IconWrapper>
          <img src={playIcon} alt="play" width={24} height={24} />
        </IconWrapper>
        {!!dailyTasksExpiresAt && (
          <StyledChip>
            <FontAwesomeIcon icon={iconClock} />
            <TimeLeft time={dailyTasksExpiresAt} />
          </StyledChip>
        )}
      </MissionButton>
    </Badge>
  );
};

DailyActionTaskWidget.fragments = {
  dailyActionTask: gql`
    fragment DailyActionTaskWidget_dailyActionTask on DailyActionTask {
      id
      subtasks {
        id
        aasmState
      }
    }
  ` as TypedDocumentNode<DailyActionTaskWidget_dailyActionTask>,
};
